import { type DateRange, getRange } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { type TemplateResult, html } from "lit";
import { createRoute, lazyElement } from "../../../lib/tsr/router";
import { redirectTo } from "../../app-router-context";
import { employeesAllRoute } from "../../app-routes";
import { balancesQuery } from "./balances.queries";
import type { EmployeesAllLayoutRoute } from "./employees-all.route";

function validateRange(search: Record<string, unknown>): Partial<DateRange> {
    return { from: search.from as DateString | undefined, to: search.to as DateString | undefined };
}

function defaultRange(path: string, search: Partial<DateRange>): void {
    if (!search.from || !search.to) {
        redirectTo(path, { ...search, ...getRange(new Date(), "month") });
    }
}

const loaderDeps = ({ search }: { search: Partial<DateRange> }) => ({ from: search.from!, to: search.to! });

export function createBalancesRoutes(parent: EmployeesAllLayoutRoute) {
    const timeBalance = createRoute({
        getParentRoute: () => parent,
        path: "time-balance",
        staticData: {
            title: "Arbeitszeitkonto: Alle Mitarbeiter",
            helpPage: "/handbuch/mitarbeiter/arbeitszeitkonto",
            menu: employeesAllRoute("time-balance"),
        },
        validateSearch: validateRange,
        beforeLoad: ({ search }): void => defaultRange("/employees/all/time-balance", search),
        loaderDeps,
        loader: ({ deps, context }) =>
            context.queryClient.fetchQuery({ ...balancesQuery(context.app, "time", deps), staleTime: 30_000 }),
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-time-balance-all
                class="fullbleed"
                .balances=${match.loaderData ?? []}
                .range=${{ from: match.search.from, to: match.search.to }}
            ></ptc-employees-time-balance-all>`,
    }).lazy(lazyElement(() => import("./employees-time-balance-all")));

    const vacationBalance = createRoute({
        getParentRoute: () => parent,
        path: "vacation-balance",
        staticData: {
            title: "Urlaubskonto: Alle Mitarbeiter",
            helpPage: "/handbuch/mitarbeiter/urlaubskonto",
            menu: employeesAllRoute("vacation-balance"),
        },
        validateSearch: validateRange,
        beforeLoad: ({ search }): void => defaultRange("/employees/all/vacation-balance", search),
        loaderDeps,
        loader: ({ deps, context }) =>
            context.queryClient.fetchQuery({ ...balancesQuery(context.app, "vacation", deps), staleTime: 30_000 }),
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-vacation-balance-all
                class="fullbleed"
                .balances=${match.loaderData ?? []}
                .range=${{ from: match.search.from, to: match.search.to }}
            ></ptc-employees-vacation-balance-all>`,
    }).lazy(lazyElement(() => import("./employees-vacation-balance-all")));

    const bonusesBalance = createRoute({
        getParentRoute: () => parent,
        path: "bonuses-balance",
        staticData: {
            title: "Zuschlagskonten: Alle Mitarbeiter",
            helpPage: "/handbuch/mitarbeiter/zuschlagskonto",
            menu: employeesAllRoute("bonuses-balance"),
        },
        validateSearch: validateRange,
        beforeLoad: ({ search }): void => defaultRange("/employees/all/bonuses-balance", search),
        loaderDeps,
        loader: ({ deps, context }) =>
            context.queryClient.fetchQuery({ ...balancesQuery(context.app, "bonuses", deps), staleTime: 30_000 }),
        component: ({ match }): TemplateResult =>
            html`<ptc-employees-bonuses-balance-all
                class="fullbleed"
                .balances=${match.loaderData ?? []}
                .range=${{ from: match.search.from, to: match.search.to }}
            ></ptc-employees-bonuses-balance-all>`,
    }).lazy(lazyElement(() => import("./employees-bonuses-balance-all")));

    return [timeBalance, vacationBalance, bonusesBalance] as const;
}
